export const CUE_ACTION = {
  VIDEO: '@@translate@@ Video',
  SHARE_SLIDES: '@@translate@@ Share Slides',
  SURVEY: '@@translate@@ Survey',
  OPEN_POLL: '@@translate@@ Open Poll',
  SHARE_POLL: '@@translate@@ Share Poll',
  SHARE_TICKER: '@@translate@@ Share Ticker',
  STOP_TICKER: '@@translate@@ Stop Ticker',
  SEED_CHAT_MESSAGE: '@@translate@@ Seed Chat Message',
  MOVE_TO_STAGE: '@@translate@@ Move to Stage',
  MOVE_OFF_STAGE: '@@translate@@ Move off Stage',
  STAGE_DIRECTION: '@@translate@@ Run of Show Notes',
  START_SESSION: '@@translate@@ Start Session',
  END_SESSION: '@@translate@@ End Session',
  SHARE: '@@translate@@ Share',
  STOP_SHARING: '@@translate@@ Stop Sharing',
  CLOSE: '@@translate@@ Close',
  LAUNCH: '@@translate@@ Launch',
  SEND_TO_CHAT: '@@translate@@ Send to Chat',
  DONE: '@@translate@@ Done',
  TIME_LEFT: '@@translate@@ Time left: {time}'
};

export const CUE_PROPERTY_TYPES = {
  VIDEO: 'VIDEO_ASSET_ID',
  SURVEY: 'SURVEY_ASSET_ID',
  MESSAGE: 'MESSAGE',
  POLL: 'POLL_POST_ID',
  SLIDE: 'SLIDE_ASSET_ID',
  SLIDE_NUMBER: 'SLIDE_NUMBER',
  TICKER_MESSAGE: 'TICKER_MESSAGE',
  TICKER_URL: 'TICKER_URL',
  SPEAKER: 'SPEAKER_ID',
  SURVEY_OPEN_TIME: 'SURVEY_OPEN_TIME',
  SURVEY_DURATION: 'SURVEY_DURATION',
  SURVEY_LAUNCH_ENUM: 'SURVEY_LAUNCH_ENUM',
  SURVEY_TOGGLE: 'SURVEY_TOGGLE'
} as const;

export const SESSION_TYPE = {
  BROADCAST: 'BROADCAST'
} as const;

export const THIRTY_SECONDS = 30 * 1000;

export const SURVEY_TIMEOUT_THRESHOLD = '01';

export const SESSION_CUE_START_ORDER = -1;
export const SESSION_CUE_END_ORDER = 10000;
export const SESSION_ACTIONS = {
  START: 'SESSION_START',
  END: 'SESSION_END'
} as const;
export type SessionAction = (typeof SESSION_ACTIONS)[keyof typeof SESSION_ACTIONS];

// Action tokens used when creating cues (must match backend Action enum strings)
export const LIVE_RUN_OF_SHOW_ACTIONS = {
  SHARE_VIDEO: 'SHARE_VIDEO',
  SHARE_SLIDE: 'SHARE_SLIDE',
  LAUNCH_SURVEY: 'LAUNCH_SURVEY',
  OPEN_POLL: 'OPEN_POLL',
  SHARE_POLL: 'SHARE_POLL',
  SEED_CHAT_MESSAGE: 'SEED_CHAT_MESSAGE',
  STAGE_DIRECTION: 'STAGE_DIRECTION',
  SHARE_TICKER: 'SHARE_TICKER',
  MOVE_SPEAKERS_TO_STAGE: 'MOVE_SPEAKERS_TO_STAGE',
  MOVE_SPEAKERS_OFF_STAGE: 'MOVE_SPEAKERS_OFF_STAGE'
} as const;

export const RESET_CUES = {
  BUTTON_LABEL: '@@translate@@ Reset Run of Show',
  DIALOG_TITLE: '@@translate@@ Reset All Cues',
  DIALOG_MESSAGE: '@@translate@@ This action will reset all cues in the run of show.',
  CONFIRM_TEXT: '@@translate@@ Confirm',
  CANCEL_TEXT: '@@translate@@ Cancel',
  DIALOG_CLASS: 'reset-cues-dialog'
};

export const DELETE_ALL_CUES = {
  BUTTON_LABEL: '@@translate@@ Delete All Cues',
  DIALOG_TITLE: '@@translate@@ Delete All Cues',
  DIALOG_MESSAGE: '@@translate@@ This action will clear your run of show queue. This cannot be undone.',
  CONFIRM_TEXT: '@@translate@@ Confirm',
  CANCEL_TEXT: '@@translate@@ Cancel',
  DIALOG_CLASS: 'delete-all-cues-dialog'
};

export const TICKER_VALIDATION = {
  MIN_MESSAGE_LENGTH: 0,
  MAX_MESSAGE_LENGTH: 50
} as const;

export const ADD_CUE_CONFIG = {
  SLIDES_LABEL: '@@translate@@ Slides',
  SLIDES_DESCRIPTION: '@@translate@@ Share a slide from uploaded slide deck',
  VIDEO_LABEL: '@@translate@@ Video',
  VIDEO_DESCRIPTION: '@@translate@@ Share a video from uploaded video assets',
  SURVEY_LABEL: '@@translate@@ Survey',
  SURVEY_DESCRIPTION: '@@translate@@ Launch a survey for attendees',
  OPEN_POLL_LABEL: '@@translate@@ Open Poll',
  OPEN_POLL_DESCRIPTION: '@@translate@@ Open or close polls for voting',
  SHARE_POLL_LABEL: '@@translate@@ Share Poll',
  SHARE_POLL_DESCRIPTION: '@@translate@@ Share or unshare poll results on stage',
  SEED_CHAT_MESSAGE_LABEL: '@@translate@@ Seed Chat Message',
  SEED_CHAT_MESSAGE_DESCRIPTION: '@@translate@@ Post a message to the chat',
  ADD_CUE_BUTTON: '@@translate@@ Add Cue',
  CANCEL_BUTTON: '@@translate@@ Cancel',
  RUN_OF_SHOW_NOTES: '@@translate@@ Run of Show Notes',
  SHARE_TICKER: '@@translate@@ Share Ticker',
  MOVE_TO_STAGE: '@@translate@@ Move to Stage',
  MOVE_OFF_STAGE: '@@translate@@ Move off Stage'
};

export const ERROR_MESSAGES = {
  NO_SESSION_ID: 'No session ID available for {action}ing session',
  EXECUTE_NOT_IMPLEMENTED: 'Execute action not implemented for this cue type',
  NO_POLL_ID_MANAGEMENT: 'No poll ID available for poll management',
  NO_POLL_ID_SHARING: 'No poll ID available for poll sharing',
  NO_SLIDE_ID: 'No slide ID available for slide sharing',
  NO_EXECUTE_FUNCTION: 'No execute function found for cue action: {action}',
  OPERATION_REQUIRES_BROADCAST_ID: 'Operation requires broadcast ID but none found',
  FAILED_TO_LOAD_CUES: 'Failed to load cues:',
  FAILED_TO_MANAGE_SESSION_CUES: 'Failed to manage session cues:',
  SESSION_CUES_CANNOT_BE_CREATED: 'Session cues cannot be manually created - they are auto-managed in manual mode',
  SESSION_CUES_CANNOT_BE_DELETED: 'Session cues cannot be deleted - they are system managed',
  FAILED_TO_DELETE_CUE: 'Failed to delete cue:',
  FAILED_TO_REORDER_CUE: 'Failed to reorder cue:'
};

export const ACTION_TYPES = {
  START_MANUAL_SESSION: 'startManualSessionAction',
  END_MANUAL_SESSION: 'endManualSessionAction',
  UNPUBLISH_SURVEY_DIALOG: 'unPublishSurveyDialog',
  SHOW_PUBLISH_SURVEY_DIALOG: 'showPublishSurveyDialog'
};

export const SELECTOR_LABELS = {
  UNKNOWN_VIDEO: '@@translate@@ Unknown Video',
  UNKNOWN_SLIDE_DECK: '@@translate@@ Unknown Slide Deck',
  START_FROM_SLIDE: '@@translate@@ Start from slide',
  SELECT_A_VIDEO: '@@translate@@ Select a Video',
  SELECT_A_SURVEY: '@@translate@@ Select a Survey',
  SELECT_A_SLIDE_DECK: '@@translate@@ Select a Slide Deck',
  START_FROM_SLIDE_LABEL: '@@translate@@ Start From Slide:',
  OPEN_SURVEY_LABEL: '@@translate@@ Open Survey',
  SURVEY_DURATION_LABEL: '@@translate@@ Duration',
  MINUTES_UNIT: '@@translate@@ minutes'
};

export const SURVEY_LAUNCH_OPTIONS = {
  AFTER_SESSION_BEGINS: 'AFTER_SESSION_BEGINS',
  BEFORE_SESSION_ENDS: 'BEFORE_SESSION_ENDS'
} as const;

export const SURVEY_LAUNCH_LABELS = {
  AFTER_SESSION_BEGINS: '@@translate@@ After session begins',
  BEFORE_SESSION_ENDS: '@@translate@@ Before session ends'
};
